import BadgePill from 'components/common/badges/BadgePill'
import useInterval from 'hooks/useInterval'
import { MDBIcon } from 'mdbreact'
import React, { useState } from 'react'
import {
  businessHoursBetween,
  formatHoursAtLocationDays,
  parseUtcDateTime,
} from 'utils/tableUtils'

// Hours at Location (ICE-27) flag — a self-contained LEAF that owns its own once-a-minute tick so
// the flag flips on (and its "Days at Location" label advances) without re-rendering the table or
// the contract page around it. Both flag sites embed this. In the contracts table it is embedded
// once per row INSIDE the row data; that row object's reference does not change on a tick, so
// MDBDataTableV5 never rebuilds rows — only the mounted flag leaves re-render themselves.
//
// Anchors (all UTC, straight from the backend):
//   startAt     - datetime_at_location_start: instant the current location run began.
//   offsetHours - business_hours_at_location_offset: held (ignored) business hours to subtract.
//   flagAt      - datetime_at_location_flag: literal instant flagging begins (null = never flags).
// Flagged when now > flagAt; displayed days = (businessHoursBetween(start, now) - offset) / 24.
const TICK_MS = 60 * 1000

const LocationFlag = ({ startAt, offsetHours, flagAt, variant = 'cell' }) => {
  const flagDate = parseUtcDateTime(flagAt)
  const startDate = parseUtcDateTime(startAt)

  const [now, setNow] = useState(() => Date.now())
  // Only rows that can ever flag run a timer (delay = null disables useInterval). Once mounted and
  // flagged, the tick keeps the day count fresh as business hours accrue.
  useInterval(() => setNow(Date.now()), flagDate ? TICK_MS : null)

  // never flags, missing the start anchor, or the threshold instant hasn't been reached yet ->
  // render nothing. Guarding on startDate avoids showing a flag with a 0/negative day count when
  // the start anchor is absent (businessHoursBetween would treat a null start as 0).
  if (!flagDate || !startDate || now <= flagDate.getTime()) return null

  // clamp at 0: a held offset larger than the hours accrued so far would otherwise show negative days
  const netHours = Math.max(
    0,
    businessHoursBetween(startDate, now) - (Number(offsetHours) || 0)
  )
  const label = `${formatHoursAtLocationDays(netHours)} Days at Location`

  if (variant === 'badge') {
    return (
      <BadgePill themeColor="red" lightenColor={0.375}>
        <MDBIcon icon="flag" className="mr-2 text-danger" />
        {/* secondary-color-text: app's secondary color (#2d3848, !important — overrides the
            badge's white) */}
        <span className="secondary-color-text">{label}</span>
      </BadgePill>
    )
  }

  // 'cell' variant (contracts table): the bare red flag with the day count as a tooltip.
  return (
    <span className="text-danger" title={label}>
      <MDBIcon icon="flag" />
    </span>
  )
}

export default LocationFlag
